/*
 * This file is part of the Det Norske Teatret application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useState, type ComponentProps } from 'react';
import classnames from 'classnames';
import NodeTransition from 'components/NodeTransition/NodeTransition';
import Picture from 'components/Picture/Picture';
import VideoBackground from 'components/VideoBackground/VideoBackground';
import Text from './Text/Text';
import './Story.scss';

const DEFAULT_PAGES: Page[] = [];

interface Page {
  id: string | number;
  image: ComponentProps<typeof Picture>;
  video?: ComponentProps<typeof VideoBackground>;
  texts: string[];
}

interface StoryProps {
  pages?: Page[];
}

/**
 * This is the Story component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */
function Story({ pages = DEFAULT_PAGES }: StoryProps) {
  const [current, setCurrent] = useState(0);

  const images = pages.map(({ image }) => image);
  const videos = pages.map(({ video }) => video ?? null);

  const texts = pages.reduce<{ image: number; text: string }[]>(
    (accumulator, currentValue, index) => {
      const texts = currentValue.texts.map(text => ({ image: index, text }));
      return [...accumulator, ...texts];
    },
    []
  );

  return (
    <div className={classnames('story')} appear="true">
      <div className="images">
        {images.map((props, index) => (
          <NodeTransition
            key={index}
            in={index === current}
            timeout={1000}
            appear
          >
            <div className="media">
              <Picture {...props} />
              {videos[index] ? <VideoBackground {...videos[index]} /> : null}
            </div>
          </NodeTransition>
        ))}
      </div>

      <div className="texts">
        {texts.map(({ image, ...props }, index) => (
          <Text
            key={index}
            {...props}
            onInview={() => {
              setCurrent(image);
            }}
          />
        ))}
      </div>
    </div>
  );
}

export default Story;
